import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../../state'
import {convertUnit, handleFloatInput, tempUnitStr} from '../../../config/utils'
import {Text, ValueInputWithRange} from '../../../sharedComponents/components'
import {EventStatusCode, PerformableTask} from '../../../state/performTask/state'
import PerformTaskForm from './PerformTaskForm'
import PerformTaskTitle from './PerformTaskTitle'
import {FormContainer} from './PerformCoolerCoolingTask'

interface Props {
  task: PerformableTask
  setValue: any
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  payload: any
  handleRemove: () => void
  hasCurrentEvent?: boolean
  currentEventId?: string
}

const PerformDishwasherTemperatureTask = ({
  task,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  payload,
  handleRemove,
  hasCurrentEvent,
  currentEventId
}: Props) => {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  const [isRequired, setIsRequired] = useState(true)
  const configurations = (task as any).dishwasherConfigurations || []
  const temperatureLabelKeys: Record<string, string> = {
    prewash: 'tasks:labels.dishwasher.prewashTemperature',
    wash: 'tasks:labels.dishwasher.washTemperature',
    rinse: 'tasks:labels.dishwasher.rinseTemperature'
  }
  const temperatureOrder = ['prewash', 'wash', 'rinse']
  const measurementName = (dishwasherName: string, temperatureType: string) => `${dishwasherName} - ${temperatureType}`
  const measurements: {dishwasherName: string; temperatureType: string; value: string | number}[] =
    payload.measurements || []
  const setMeasurement = (dishwasherName: string, temperatureType: string, value: string) => {
    const next = measurements.filter(item => {
      const matchesConfiguration = item.dishwasherName === dishwasherName
      const matchesDisplayName = item.dishwasherName === measurementName(dishwasherName, temperatureType)
      return !(matchesConfiguration || matchesDisplayName) || item.temperatureType !== temperatureType
    })
    setValue('measurements', [
      ...next,
      {
        dishwasherName: measurementName(dishwasherName, temperatureType),
        temperatureType,
        value: handleFloatInput(value)
      }
    ])
  }

  const handleRemoveTask = () => {
    setIsRequired(false)
    if (handleRemove) {
      handleRemove()
    }
  }

  return (
    <FormContainer>
      <PerformTaskTitle task={task} />
      {configurations.map((configuration: any) =>
        [...configuration.temperatures]
          .sort(
            (first: any, second: any) => temperatureOrder.indexOf(first.type) - temperatureOrder.indexOf(second.type)
          )
          .map((temperature: any) => {
            const current = measurements.find(
              item =>
                (item.dishwasherName === configuration.name ||
                  item.dishwasherName === measurementName(configuration.name, temperature.type)) &&
                item.temperatureType === temperature.type
            )
            return (
              <ValueInputWithRange
                key={`${configuration.name}-${temperature.type}`}
                id={`${configuration.name}-${temperature.type}`}
                type="number"
                value={current?.value?.toString() || ''}
                label={
                  <>
                    <Text size="M">{t(temperatureLabelKeys[temperature.type], temperature.type)}</Text>
                    <br />
                    <Text size="S">{t('tasks:labels.measuredValue', 'Measured value')} *</Text>
                  </>
                }
                minValue={convertUnit(state.site!, temperature.minValue)}
                maxValue={convertUnit(state.site!, temperature.maxValue)}
                unit={tempUnitStr(state.site!.temperatureUnit)}
                required={isRequired}
                onChange={event => setMeasurement(configuration.name, temperature.type, event.target.value)}
                message={{
                  success: t('tasks:inputWithRange.withinTarget', 'The value is within target limits'),
                  error: t('tasks:inputWithRange.not.withInTarget', 'The value is not within target')
                }}
              />
            )
          })
      )}
      <PerformTaskForm
        onAssetSaved={onAssetSaved}
        onAssetRemoved={onAssetRemoved}
        onInputChange={setValue}
        payload={payload}
        handleRemove={
          !!task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS ? handleRemoveTask : undefined
        }
        setValue={setValue}
        confirm={
          task.scheduleInstance || task.status === EventStatusCode.IN_PROGRESS
            ? t(
                'tasks:labels.confirmRemoveTask',
                'Are you sure you want to delete this task? This will also remove any associated resolved alarms.'
              )
            : undefined
        }
        hasCurrentEvent={!!hasCurrentEvent}
        currentEventId={currentEventId}
      />
    </FormContainer>
  )
}

export default PerformDishwasherTemperatureTask
